/* ==========================================================================
   plan-discount.js
   Applies the selected plan's discount code.

   Deliberately a SEPARATE file from plan-selector.js. It only listens for the
   `plan-selector:change` event that plan-selector.js already dispatches and
   never touches the DOM of the plan cards, so a failure in here cannot stop
   the cards or the benefits list from rendering.

   What "applying" means, in three layers (all three run, cheapest first):
     1. GET /discount/CODE  -> Shopify sets the discount cookie for the session,
        so the code is live in cart and checkout even if the customer navigates.
     2. dispatch `discountCodeSelected` -> the theme's smart-cart-custom.js
        listens for this and puts the code on its /checkout URL.
     3. rewrite every checkout link/form to carry ?discount=CODE, as a backstop
        for any path the first two miss.

   Load AFTER plan-selector.js in sections/main-product.liquid:
     <script src="{{ 'plan-discount.js' | asset_url }}" defer="defer"></script>
   ========================================================================== */

(function () {
  var STORAGE_KEY = 'planDiscountCode';
  var applied = '';

  function log() {
    if (window.__planSelectorDebug) console.log.apply(console, ['[plan-discount]'].concat([].slice.call(arguments)));
  }

  function stored() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function remember(code) {
    try {
      sessionStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      /* private mode - the cookie from step 1 still carries it */
    }
  }

  /* ---- 1. set the discount cookie ---------------------------------------
     /discount/CODE is Shopify's own endpoint. Redirecting to /cart.js keeps
     the response tiny and same-origin. Failures are non-fatal: layers 2 and 3
     still carry the code to checkout. */
  function setCookie(code) {
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (res) {
        log('cookie set', code, res.status);
      })
      .catch(function (err) {
        log('cookie failed', code, err);
      });
  }

  /* ---- 2. tell the theme ------------------------------------------------
     available-offers.js / smart-cart-custom.js speak `discountCodeSelected`.
     Different builds of that pair read a different key off detail, so send
     all three spellings rather than guessing. */
  function notifyTheme(code) {
    var detail = { code: code, discountCode: code, discount_code: code };
    document.dispatchEvent(new CustomEvent('discountCodeSelected', { detail: detail, bubbles: true }));
    window.dispatchEvent(new CustomEvent('discountCodeSelected', { detail: detail }));
    window.selectedDiscountCode = code;
    window.appliedDiscountCode = code;
  }

  /* ---- 3. rewrite checkout destinations --------------------------------- */
  function withDiscount(url, code) {
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('discount', code);
      return u.pathname + u.search + u.hash;
    } catch (e) {
      return url;
    }
  }

  function isCheckoutHref(href) {
    return !!href && /\/checkout(\?|$|\/)/.test(href);
  }

  function rewriteLinks(code) {
    document.querySelectorAll('a[href*="/checkout"]').forEach(function (a) {
      if (isCheckoutHref(a.getAttribute('href'))) a.setAttribute('href', withDiscount(a.getAttribute('href'), code));
    });
  }

  /* Cart forms POST to /cart with name="checkout". A hidden `discount` input
     on that form is honoured by Shopify. */
  function stampForms(code) {
    document.querySelectorAll('form[action^="/cart"], form[action*="/checkout"]').forEach(function (form) {
      if (form.action.indexOf('/cart/add') !== -1) return;
      var input = form.querySelector('input[name="discount"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        form.appendChild(input);
      }
      input.value = code;
    });
  }

  function apply(code, force) {
    if (!code) return;
    if (code === applied && !force) {
      rewriteLinks(code);
      stampForms(code);
      return;
    }
    applied = code;
    remember(code);
    log('applying', code);
    setCookie(code);
    notifyTheme(code);
    rewriteLinks(code);
    stampForms(code);
    /* Cart drawers re-render their checkout button after the cookie call, so
       re-stamp once things have settled. */
    setTimeout(function () {
      rewriteLinks(code);
      stampForms(code);
    }, 500);
  }

  /* The plan cards fire this. It is the only hook into the selector. */
  document.addEventListener('plan-selector:change', function (e) {
    var code = (e.detail && e.detail.discountCode) || '';
    if (code) apply(code);
  });

  /* Last-chance re-stamp: anything that opens checkout gets the current code,
     even if the theme rebuilt the button after we last wrote to it. */
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target.closest) return;
      var code = applied || stored();
      if (!code) return;
      var trigger = e.target.closest('a[href*="/checkout"], [name="checkout"], .cart__checkout-button, .cart__checkout');
      if (!trigger) return;
      stampForms(code);
      if (trigger.tagName === 'A' && isCheckoutHref(trigger.getAttribute('href'))) {
        trigger.setAttribute('href', withDiscount(trigger.getAttribute('href'), code));
      }
    },
    true
  );

  /* Pick up the code the preselected plan already carries, without waiting for
     a click. plan-selector.js selects on DOMContentLoaded; run after it. */
  function initFromSelection() {
    var radio = document.querySelector('plan-option.is-selected .plan-option__radio');
    var code = (radio && radio.dataset.discountCode) || stored();
    if (code) apply(code, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(initFromSelection, 0);
    });
  } else {
    setTimeout(initFromSelection, 0);
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(initFromSelection, 0);
  });

  window.PlanDiscount = { apply: apply, current: function () { return applied || stored(); } };
})();
